import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, LinearProgress, Link, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { GlowCard, SectionHeader } from '@norvix/ui';
import { formatExactMinorUnits } from '@norvix/utils';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { reportCommissionsApi, type CommissionReport } from '../../api/report-commissions.api.js';
import type { ReportCurrency } from '../../api/report-transactions.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { CommissionAccountsSection, transferredByCurrency, useTransfersInPeriod } from './CommissionAccountsSection.js';
import { ReportExportMenu, useReportExport } from './ReportExportMenu.js';
import { ReportPeriodFields, useReportPeriod } from './ReportPeriodControls.js';
import { TYPE_LABEL } from './TransactionsReportTab.js';
import { amountStyle, bucketLabel, effectiveRate, money, periodLabel } from './report-format.js';

/**
 * Reports → Commissions (DEV-128, stage 1): what customers paid us, per month,
 * per commission type and per customer. Provider costs and margin arrive with DEV-144.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PROVIDER_LABEL: Record<string, string> = { GURUPAY: 'GuruPay', BANKERA: 'Bankera', MANUAL: 'Manual bank', NORVIX: 'Norvix' };

interface Option { value: string; label: string }
const FILTERS: Array<{ key: string; label: string; options: Option[] }> = [
  { key: 'provider', label: 'Provider', options: Object.entries(PROVIDER_LABEL).map(([value, label]) => ({ value, label })) },
  { key: 'type', label: 'Type', options: Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label })) },
  { key: 'direction', label: 'Direction', options: [{ value: 'IN', label: 'Incoming' }, { value: 'OUT', label: 'Outgoing' }] },
];

// Chart geometry only. Every displayed amount uses the exact integer strings.
const chartValue = (minor: string | null): number | null => minor === null ? null : Number(minor) / 100;

function MonthlyCharts({ data, currency }: { data: CommissionReport; currency: string }): JSX.Element {
  const [tableVisible, setTableVisible] = useState(false);
  const months = data.monthly.map(bucketLabel);
  const yAxis = [{ valueFormatter: (value: number): string => new Intl.NumberFormat('en', { notation: 'compact' }).format(value) }];
  const margin = { left: 60, right: 10, top: 20, bottom: 30 };
  return <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
      <Typography variant="h6">By month</Typography>
      <Button size="small" aria-expanded={tableVisible} aria-controls="commission-monthly-figures" onClick={() => setTableVisible(!tableVisible)}>
        {tableVisible ? 'Hide figures' : 'View figures'}</Button>
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'repeat(2,minmax(0,1fr))' }, gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Customer commissions ({currency})</Typography>
        <Box role="img" aria-label={`Monthly customer commissions in ${currency}. Exact values are available under View figures.`} sx={{ width: '100%', minWidth: 0 }}>
          <BarChart height={260} xAxis={[{ scaleType: 'band', data: months }]} yAxis={yAxis} margin={margin} skipAnimation
            series={[{ label: 'Commissions', data: data.monthly.map((row) => chartValue(row.feeMinor)), color: 'var(--aurora-500)',
              valueFormatter: (_value, context) => money(data.monthly[context.dataIndex]?.feeMinor ?? null, currency) }]}
            slotProps={{ legend: { hidden: true } }} />
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Turnover ({currency})</Typography>
        <Box role="img" aria-label={`Monthly turnover in ${currency}. Exact values are available under View figures.`} sx={{ width: '100%', minWidth: 0 }}>
          <BarChart height={260} xAxis={[{ scaleType: 'band', data: months }]} yAxis={yAxis} margin={margin} skipAnimation
            series={[{ label: 'Turnover', data: data.monthly.map((row) => chartValue(row.turnoverMinor)), color: 'var(--ice-300)',
              valueFormatter: (_value, context) => money(data.monthly[context.dataIndex]?.turnoverMinor ?? null, currency) }]}
            slotProps={{ legend: { hidden: true } }} />
        </Box>
      </Box>
    </Box>
    <Collapse in={tableVisible} id="commission-monthly-figures"><TableContainer><Table size="small" aria-label="Exact monthly figures">
      <TableHead><TableRow><TableCell>Month (UTC)</TableCell><TableCell align="right">Operations</TableCell><TableCell align="right">Turnover</TableCell>
        <TableCell align="right">Commissions</TableCell></TableRow></TableHead>
      <TableBody>{data.monthly.map((row) => <TableRow key={row.month}>
        <TableCell component="th" scope="row">{bucketLabel(row)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{row.operations.toLocaleString(APP_LOCALE)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(row.turnoverMinor, currency)}</TableCell>
        <TableCell align="right" sx={{ ...amountStyle, fontWeight: 600 }}>{money(row.feeMinor, currency)}</TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer></Collapse>
  </Paper>;
}

function ByFeeTable({ data, currency }: { data: CommissionReport; currency: string }): JSX.Element {
  return <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>By commission type</Typography>
    <TableContainer><Table size="small" aria-label="By commission type">
      <TableHead><TableRow><TableCell>Commission</TableCell><TableCell align="right">Operations</TableCell><TableCell align="right">Turnover</TableCell>
        <TableCell align="right">Commissions</TableCell><TableCell align="right">Rate</TableCell></TableRow></TableHead>
      <TableBody>{data.byFee.map((row) => <TableRow key={row.feeName} hover>
        <TableCell sx={{ maxWidth: 260, overflowWrap: 'anywhere' }}>{row.feeName}</TableCell>
        <TableCell align="right" sx={amountStyle}>{row.operations.toLocaleString(APP_LOCALE)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(row.turnoverMinor, currency)}</TableCell>
        <TableCell align="right" sx={{ ...amountStyle, fontWeight: 600 }}>{money(row.feeMinor, currency)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{effectiveRate(row.feeMinor, row.turnoverMinor)}</TableCell>
      </TableRow>)}
      {data.byFee.length === 0 && <TableRow><TableCell colSpan={5}>No commissions.</TableCell></TableRow>}</TableBody>
    </Table></TableContainer>
  </Paper>;
}

interface ByCustomerProps { data: CommissionReport; currency: string; canOpenCustomer: boolean; onPick: (key: string) => void }

function ByCustomerTable({ data, currency, canOpenCustomer, onPick }: ByCustomerProps): JSX.Element {
  return <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>By customer</Typography>
    <TableContainer><Table size="small" aria-label="By customer">
      <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Type</TableCell><TableCell align="right">Operations</TableCell>
        <TableCell align="right">Turnover</TableCell><TableCell align="right">Commissions</TableCell><TableCell align="right">Rate</TableCell></TableRow></TableHead>
      <TableBody>{data.byCustomer.map((row) => <TableRow key={row.key} hover>
        <TableCell sx={{ maxWidth: 260 }}>
          {UUID.test(row.key) ? <>
            <Button size="small" onClick={() => onPick(row.key)} title="Show only this customer"
              sx={{ p: 0, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', overflowWrap: 'anywhere' }}>{row.label}</Button>
            {canOpenCustomer && <Link component={RouterLink} to={`/customers/${row.key}`} variant="caption" display="block" aria-label={`Open ${row.label}`}>Open customer</Link>}
          </> : <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{row.label || 'Not attributed'}</Typography>}
        </TableCell>
        <TableCell>{row.customerType === 'B' ? 'Business' : row.customerType === 'C' ? 'Personal' : '—'}</TableCell>
        <TableCell align="right" sx={amountStyle}>{row.operations.toLocaleString(APP_LOCALE)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(row.turnoverMinor, currency)}</TableCell>
        <TableCell align="right" sx={{ ...amountStyle, fontWeight: 600 }}>{money(row.feeMinor, currency)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{effectiveRate(row.feeMinor, row.turnoverMinor)}</TableCell>
      </TableRow>)}
      {data.byCustomer.length === 0 && <TableRow><TableCell colSpan={6}>No customers.</TableCell></TableRow>}</TableBody>
    </Table></TableContainer>
  </Paper>;
}

export function CommissionsReportTab(): JSX.Element {
  const { can } = useAuth();
  const showAccounts = can('client-money.view');
  // Every role that reads this report may open the customer it names (masked below MLRO).
  const canOpenCustomer = can('customers.view');
  const [params, setParams] = useSearchParams();
  const period = useReportPeriod();
  const { from, through } = period;
  const raw = params.get('currency');
  const currency: ReportCurrency = raw === 'USD' || raw === 'CAD' ? raw : 'EUR';
  const customerId = params.get('customerId') ?? '';
  const update = (values: Record<string, string>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) { if (value) next.set(key, value); else next.delete(key); }
    setParams(next);
  };
  const validDates = period.error === null;
  const filterValues = Object.fromEntries(FILTERS.map((f) => [f.key, params.get(f.key) ?? '']));
  const query = new URLSearchParams(Object.entries({ from, through, currency, customerId, ...filterValues })
    .filter(([, value]) => value !== '')).toString();
  const report = useQuery({ queryKey: ['report-commissions', query], queryFn: () => reportCommissionsApi.read(query),
    enabled: validDates, staleTime: 30_000, retry: 1 });
  const transfers = useTransfersInPeriod(from, through, showAccounts && validDates);
  const data = report.data;
  const exporter = useReportExport((format) => reportCommissionsApi.export(query, format),
    `norvix-commissions-report-${from}-${through}-${currency}`);
  const refresh = (): void => { void report.refetch(); if (showAccounts) void transfers.refetch(); };
  const customerLabel = data?.byCustomer.find((c) => c.key === customerId)?.label ?? customerId;
  const anyFilter = customerId !== '' || Object.values(filterValues).some((v) => v !== '');
  const transferred = transfers.data ? transferredByCurrency(transfers.data.items) : null;

  return <Stack spacing={3} sx={{ minWidth: 0 }}>
    <Box>
      <SectionHeader title="Commissions" description="What customers paid in commissions, by month, by commission type and by customer." actions={
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} disabled={report.isFetching || !validDates} onClick={refresh}>Refresh</Button>
          <ReportExportMenu exporter={exporter} disabled={!data || report.isFetching || !validDates} />
        </Stack>} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <ReportPeriodFields period={period} onChange={update} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Show in</Typography>
          <ToggleButtonGroup exclusive size="small" color="primary" value={currency} aria-label="Reporting currency"
            onChange={(_, value: ReportCurrency | null) => { if (value) update({ currency: value }); }}>
            <ToggleButton value="EUR">EUR</ToggleButton><ToggleButton value="USD">USD</ToggleButton><ToggleButton value="CAD">CAD</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Box>

    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' }, gap: 2 }}>
        {FILTERS.map((f) => <TextField key={f.key} select size="small" label={f.label} value={filterValues[f.key]}
          InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} onChange={(event) => update({ [f.key]: event.target.value })}>
          <MenuItem value="">All</MenuItem>
          {f.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>)}
      </Box>
      {anyFilter && <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        {customerId !== '' && <Chip label={`Customer: ${customerLabel}`} onDelete={() => update({ customerId: '' })} />}
        <Button size="small" onClick={() => update({ customerId: '', ...Object.fromEntries(FILTERS.map((f) => [f.key, ''])) })}>Clear filters</Button>
      </Stack>}
    </Paper>

    {period.error !== null && <Alert severity="warning">{period.error}</Alert>}
    {exporter.error && <Alert severity="error" onClose={exporter.clearError}>{exporter.error}</Alert>}
    {report.isError && <Alert severity="error">{report.error instanceof Error ? report.error.message : 'The report could not be loaded.'} Use Refresh to retry.</Alert>}
    {report.isLoading && <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={22} /><Typography>Loading commissions…</Typography></Stack>}
    {data && validDates && <>
      {report.isFetching && <LinearProgress aria-label="Refreshing commissions" />}
      {data.totals.missingRates > 0 && <Alert severity="warning">
        {data.totals.missingRates} {data.totals.missingRates === 1 ? 'amount has' : 'amounts have'} no eligible ECB rate, so converted totals are unavailable. The export still shows the original amounts.
      </Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', xl: `repeat(${showAccounts ? 4 : 3},minmax(0,1fr))` }, gap: 2 }}>
        {[
          { label: 'Turnover', value: money(data.totals.turnoverMinor, currency), hint: `Completed payments, in and out · ${data.totals.operations.toLocaleString(APP_LOCALE)} operations` },
          { label: 'Customer commissions', value: money(data.totals.feeMinor, currency),
            hint: `Payments ${money(data.totals.paymentFeeMinor, currency)} · Stand-alone ${money(data.totals.standaloneFeeMinor, currency)}` },
          { label: 'Effective rate', value: effectiveRate(data.totals.paymentFeeMinor, data.totals.turnoverMinor), hint: 'Commissions on completed payments ÷ turnover' },
          ...(showAccounts ? [{ label: 'Transferred to revenue account',
            value: transfers.isError ? 'Unavailable' : transferred === null ? '…'
              : transferred.length === 0 ? '0' : transferred.map((t) => `${formatExactMinorUnits(t.cents, t.currency, true)} ${t.currency}`).join(' · '),
            hint: 'Settled transfers in the period, original currencies' }] : []),
        ].map((tile) => <GlowCard key={tile.label} style={{ padding: 20, borderRadius: 12 }}>
          <Typography variant="body2" color="text.secondary">{tile.label}</Typography>
          <Typography sx={{ fontSize: { xs: 24, lg: 26 }, fontWeight: 700, fontVariantNumeric: 'tabular-nums', mt: 1, overflowWrap: 'anywhere' }}>{tile.value}</Typography>
          <Typography variant="caption" color="text.secondary">{tile.hint}</Typography>
        </GlowCard>)}
      </Box>

      <MonthlyCharts data={data} currency={currency} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,2fr) minmax(0,3fr)' }, gap: 3 }}>
        <ByFeeTable data={data} currency={currency} />
        <ByCustomerTable data={data} currency={currency} canOpenCustomer={canOpenCustomer} onPick={(key) => update({ customerId: key })} />
      </Box>
    </>}

    {showAccounts && validDates && <CommissionAccountsSection from={from} through={through} transfers={transfers} />}

    {data && validDates && <Typography variant="caption" color="text.secondary">
      Covers {periodLabel(from, through)} (UTC, inclusive).{' '}
      {data.refreshedAt === null
        ? 'The reporting table is being built for the first time; figures may be incomplete.'
        : `Data as of ${new Date(data.refreshedAt).toLocaleString(APP_LOCALE)}.`}{' '}
      Amounts in {currency} use the ECB rate dated on or before the execution date (at most seven days old). Provider costs and margin come later (DEV-144).
    </Typography>}
  </Stack>;
}
